import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import vert from "../shaders/drone.vert.glsl";
import frag from "../shaders/drone.frag.glsl";
import { assign, type Cloud } from "../formations";
import { audioBus } from "../audioBus";
import { DEFAULT_FX, type AudioFX } from "../audioFx";
import { sampleAt, transDef, type Segment, type Timeline } from "../sequencer";

const STAGGER = 0.4;

type Props = {
  timeline: Timeline;
  timing: { segments: Segment[]; total: number };
  playhead: React.MutableRefObject<number>;
  playing: boolean;
  loop: boolean;
  count: number;
  cloudsRef: React.MutableRefObject<Map<string, Cloud>>;
  cloudsVersion: number;
  brightness?: number;
  size?: number;
  /** The audio-FX rack — sequenced shows pulse to connected music too. */
  fx?: AudioFX;
};

/**
 * Renders the sequenced show. Unlike DroneField (which owns an internal morph
 * clock), this is driven entirely by `playhead`: every frame it samples the
 * timeline to "what's happening now", so the same code plays AND scrubs. On a
 * segment boundary it (re)loads the from/to clouds and untangles the pairing;
 * during a transition it drives uMorph + the transition uniforms.
 */
export function SequencerField({
  timeline, timing, playhead, playing, loop, count,
  cloudsRef, cloudsVersion, brightness = 1.5, size = 6, fx = DEFAULT_FX,
}: Props) {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  const ptsRef = useRef<THREE.Points>(null);
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const hueAngle = useRef(0);
  const n = count;

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const seed = new Float32Array(n);
    for (let i = 0; i < n; i++) seed[i] = Math.random();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute("aTarget", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute("aColorA", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute("aColorB", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const uniforms = useMemo(
    () => ({
      uMorph: { value: 1 }, uTime: { value: 0 }, uSize: { value: size }, uBrightness: { value: brightness },
      uStagger: { value: STAGGER }, uTransKind: { value: 0 }, uTransAmt: { value: 0 },
      // drone.vert multiplies by uScale UNCONDITIONALLY — an absent uniform
      // reads 0 in GLSL and collapses every drone to the origin (the "dense
      // orb" bug). Any field sharing this shader MUST provide it.
      uScale: { value: 1 },
      uAudioLevel: { value: 0 }, uAudioBeat: { value: 0 }, uShimmer: { value: 0 }, uHueShift: { value: 0 },
      uCymAmp: { value: 0 }, uCymM: { value: 0 }, uCymN: { value: 0 }, uCymMix: { value: -1 }, uCymL: { value: 1 }, uCymSpeed: { value: 0 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.uniforms.uSize.value = size;
    matRef.current.uniforms.uBrightness.value = brightness;
  }, [size, brightness]);

  // latest props for the frame loop (no re-render churn)
  const tlRef = useRef(timeline); tlRef.current = timeline;
  const timingRef = useRef(timing); timingRef.current = timing;
  const playingRef = useRef(playing); playingRef.current = playing;
  const loopRef = useRef(loop); loopRef.current = loop;
  const verRef = useRef(cloudsVersion); verRef.current = cloudsVersion;
  const segKey = useRef("");

  useFrame((_, dtRaw) => {
    const m = matRef.current;
    if (!m) return;
    const dt = Math.min(dtRaw, 1 / 30);
    m.uniforms.uTime.value += dt;

    // the audio-FX rack rides sequenced playback (same block as the fields)
    const av = audioBus.values;
    const fxc = fxRef.current;
    m.uniforms.uAudioLevel.value = fxc.pulse.on ? av.level * fxc.pulse.amt : 0;
    m.uniforms.uAudioBeat.value = fxc.pulse.on ? av.beat * fxc.pulse.amt : 0;
    m.uniforms.uShimmer.value = fxc.shimmer.on ? av.treble * fxc.shimmer.amt : 0;
    if (fxc.hue.on) hueAngle.current += av.mid * fxc.hue.amt * dt * 0.6;
    m.uniforms.uHueShift.value = fxc.hue.on ? hueAngle.current : 0;
    const swell = fxc.swell.on ? (av.bass * 0.12 + av.beat * 0.05) * fxc.swell.amt : 0;
    m.uniforms.uScale.value = 1 + swell;
    if (fxc.spin.on && ptsRef.current) ptsRef.current.rotation.y += av.bass * av.bass * 1.2 * fxc.spin.amt * dt;

    const total = timingRef.current.total;
    if (playingRef.current && total > 0) {
      playhead.current += dt;
      if (loopRef.current) {
        if (playhead.current >= total) playhead.current -= total;
      } else if (playhead.current > total) {
        playhead.current = total;
      }
    }

    const tl = tlRef.current;
    const clouds = cloudsRef.current;
    const has = (id?: string) => !!id && clouds.get(id)?.positions.length === n * 3;
    const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
    const tar = geometry.getAttribute("aTarget") as THREE.BufferAttribute;
    const ca = geometry.getAttribute("aColorA") as THREE.BufferAttribute;
    const cb = geometry.getAttribute("aColorB") as THREE.BufferAttribute;
    const flag = () => { pos.needsUpdate = tar.needsUpdate = ca.needsUpdate = cb.needsUpdate = true; };

    const st = sampleAt(tl, playhead.current);
    let morph = 1, code = 0, amt = 0;

    if (st.kind === "hold") {
      const sc = tl.scenes[st.scene];
      const key = "h:" + (sc?.id ?? "?") + ":" + verRef.current;
      if (key !== segKey.current && has(sc?.id)) {
        const c = clouds.get(sc.id)!;
        (pos.array as Float32Array).set(c.positions.subarray(0, n * 3));
        (tar.array as Float32Array).set(c.positions.subarray(0, n * 3));
        (ca.array as Float32Array).set(c.colors.subarray(0, n * 3));
        (cb.array as Float32Array).set(c.colors.subarray(0, n * 3));
        flag();
        segKey.current = key;
      }
    } else if (st.kind === "trans") {
      const from = tl.scenes[st.from], to = tl.scenes[st.to];
      const key = "t:" + (from?.id ?? "?") + ">" + (to?.id ?? "?") + ":" + verRef.current;
      if (key !== segKey.current && has(from?.id) && has(to?.id)) {
        const cf = clouds.get(from.id)!, ct = clouds.get(to.id)!;
        const a = assign(cf.positions, ct.positions, ct.colors, n);
        (pos.array as Float32Array).set(cf.positions.subarray(0, n * 3));
        (tar.array as Float32Array).set(a.positions);
        (ca.array as Float32Array).set(cf.colors.subarray(0, n * 3));
        (cb.array as Float32Array).set(a.colors);
        flag();
        segKey.current = key;
      }
      const def = transDef(st.transition);
      morph = st.progress;
      code = def.code;
      amt = def.amt;
    }

    m.uniforms.uMorph.value = morph;
    m.uniforms.uTransKind.value = code;
    m.uniforms.uTransAmt.value = amt;
  });

  return (
    <points ref={ptsRef} name="swarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
